import {
  useVirtualizer,
  Virtualizer,
  VirtualItem,
} from '@tanstack/react-virtual'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import type {
  SortingState,
  ColumnDef,
  PaginationState,
  Cell,
  HeaderGroup,
  Header,
  Row,
  Table,
  RowSelectionState,
  OnChangeFn,
  ColumnFilter,
} from '@tanstack/react-table'
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import { CSVRow } from './importer-types'
import { CSSProperties, useRef, useState, useEffect } from 'react'
import { cn } from 'components/UI/cn'
import {
  ArrowDownWideNarrowIcon,
  ArrowUpDownIcon,
  ArrowUpWideNarrowIcon,
  GripVerticalIcon,
} from 'lucide-react'
import FbButton from 'components/FbUI/FbButton'

export function ImporterDataTable({
  data,
  columns,
  setRowSelection,
  tableHeader,
  columnFiltering,
}: {
  data: CSVRow[]
  columns: ColumnDef<CSVRow>[]
  setRowSelection: OnChangeFn<RowSelectionState>
  tableHeader: React.ReactNode
  columnFiltering: ColumnFilter[]
}) {
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnOrder, setColumnOrder] = useState<string[]>(() => [
    'select',
    ...columns.map((c) => c.id!),
  ])

  console.log(columnFiltering)
  const [_rowSelection, _setRowSelection] = useState<RowSelectionState>({})

  useEffect(() => {
    if (data.length === 0) {
      return
    }

    setRowSelection(_rowSelection)
  }, [_rowSelection, sorting, data])

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  })

  // Add select column to the beginning of columns
  const columnsWithSelect: ColumnDef<CSVRow>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <div className="h-full flex items-center justify-center">
          <IndeterminateCheckbox
            {...{
              checked: table.getIsAllPageRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                if (e.target.checked) {
                  table.toggleAllPageRowsSelected(true)
                } else {
                  table.toggleAllRowsSelected(false)
                }
              },
            }}
          />
        </div>
      ),
      cell: ({ row }) => (
        <div className="h-full w-full flex items-center justify-start px-2">
          <IndeterminateCheckbox
            {...{
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              indeterminate: row.getIsSomeSelected(),
              onChange: row.getToggleSelectedHandler(),
            }}
          />
        </div>
      ),
      size: 50,
    },
    ...columns,
  ]

  const table = useReactTable({
    data,
    columns: columnsWithSelect,
    state: {
      sorting,
      pagination,
      columnOrder,
      rowSelection: _rowSelection,
      columnFilters: columnFiltering,
    },
    getRowId: (row) => row.__internal__row_id,
    enableRowSelection: true,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onColumnOrderChange: setColumnOrder,
    onRowSelectionChange: _setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  const visibleColumns = table.getVisibleLeafColumns()

  const columnVirtualizer = useVirtualizer<
    HTMLDivElement,
    HTMLTableCellElement
  >({
    count: visibleColumns.length,
    estimateSize: (index) => visibleColumns[index].getSize(),
    getScrollElement: () => tableContainerRef.current,
    horizontal: true,
    overscan: 3,
  })
  const virtualColumns = columnVirtualizer.getVirtualItems()

  let virtualPaddingLeft: number | undefined
  let virtualPaddingRight: number | undefined

  if (columnVirtualizer && virtualColumns?.length) {
    virtualPaddingLeft = virtualColumns[0]?.start ?? 0
    virtualPaddingRight =
      columnVirtualizer.getTotalSize() -
      (virtualColumns[virtualColumns.length - 1]?.end ?? 0)
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (active && over && active.id !== over.id) {
      setColumnOrder((columnOrder) => {
        const oldIndex = columnOrder.indexOf(active.id as string)
        const newIndex = columnOrder.indexOf(over.id as string)
        return arrayMove(columnOrder, oldIndex, newIndex) //this is just a splice util
      })
    }
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  )

  if (!data.length) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        No data
      </div>
    )
  }

  return (
    <>
      <div className="flex flex-row justify-start items-center gap-2">
        <div className="text-sm whitespace-nowrap">
          {Object.keys(_rowSelection).length} of{' '}
          {table.getFilteredRowModel().rows.length} Selected
        </div>
        {table.getIsAllPageRowsSelected() && !table.getIsAllRowsSelected() ? (
          <>
            <FbButton
              size="xs"
              variant="outline"
              onClick={() => table.toggleAllRowsSelected(true)}
            >
              Select All Rows in File
            </FbButton>
          </>
        ) : null}
        {table.getIsAllRowsSelected() ? (
          <span className="text-sm font-bold whitespace-nowrap">
            All Rows Selected
          </span>
        ) : null}
        {tableHeader}
      </div>
      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <div
          ref={tableContainerRef}
          className="border-l border-r border-b border-gray-200"
          style={{
            overflow: 'auto', //our scrollable table container
            position: 'relative', //needed for sticky header
            height: 'calc(100% - 70px)', //should be a fixed height
          }}
        >
          {/* Even though we're still using sematic table tags, we must use CSS grid and flexbox for dynamic row heights */}
          <table style={{ display: 'grid' }}>
            <TableHead
              columnOrder={columnOrder}
              columnVirtualizer={columnVirtualizer}
              table={table}
              virtualPaddingLeft={virtualPaddingLeft}
              virtualPaddingRight={virtualPaddingRight}
            />
            <TableBody
              columnVirtualizer={columnVirtualizer}
              table={table}
              tableContainerRef={tableContainerRef}
              virtualPaddingLeft={virtualPaddingLeft}
              virtualPaddingRight={virtualPaddingRight}
            />
          </table>
        </div>
        <div className="p-2 max-h-[60px]">
          <div className="flex flex-row items-center gap-5 justify-start">
            <div className="flex flex-row items-center gap-2">
              <FbButton
                size="xs"
                variant="outline"
                onClick={() => table.firstPage()}
                disabled={!table.getCanPreviousPage()}
              >
                {'<<'}
              </FbButton>
              <FbButton
                size="xs"
                variant="outline"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                {'<'}
              </FbButton>
              <FbButton
                size="xs"
                variant="outline"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                {'>'}
              </FbButton>
              <FbButton
                size="xs"
                variant="outline"
                onClick={() => table.lastPage()}
                disabled={!table.getCanNextPage()}
              >
                {'>>'}
              </FbButton>
            </div>
            <span className="flex items-center gap-1">
              <div>Page</div>
              <strong>
                {table.getState().pagination.pageIndex + 1} of{' '}
                {table.getPageCount().toLocaleString()}
              </strong>
            </span>
            <span className="flex items-center gap-1">
              Go to page:
              <input
                type="number"
                min="1"
                max={table.getPageCount()}
                defaultValue={table.getState().pagination.pageIndex + 1}
                onChange={(e) => {
                  const page = e.target.value ? Number(e.target.value) - 1 : 0
                  table.setPageIndex(page)
                }}
                className="border p-1 rounded w-16"
              />
            </span>
            <select
              value={table.getState().pagination.pageSize}
              onChange={(e) => {
                table.setPageSize(Number(e.target.value))
              }}
            >
              {[50, 100, 200].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </div>
        </div>
      </DndContext>
    </>
  )
}

interface TableHeadProps {
  columnVirtualizer: Virtualizer<HTMLDivElement, HTMLTableCellElement>
  table: Table<CSVRow>
  virtualPaddingLeft: number | undefined
  virtualPaddingRight: number | undefined
  columnOrder: string[]
}

function TableHead({
  columnVirtualizer,
  table,
  virtualPaddingLeft,
  virtualPaddingRight,
  columnOrder,
}: TableHeadProps) {
  return (
    <thead
      style={{
        display: 'grid',
        position: 'sticky',
        top: 0,
        zIndex: 1,
        backgroundColor: '#fdfdfd',
      }}
    >
      {table.getHeaderGroups().map((headerGroup) => (
        <TableHeadRow
          columnOrder={columnOrder}
          columnVirtualizer={columnVirtualizer}
          headerGroup={headerGroup}
          key={headerGroup.id}
          virtualPaddingLeft={virtualPaddingLeft}
          virtualPaddingRight={virtualPaddingRight}
        />
      ))}
    </thead>
  )
}

interface TableHeadRowProps {
  columnVirtualizer: Virtualizer<HTMLDivElement, HTMLTableCellElement>
  headerGroup: HeaderGroup<CSVRow>
  virtualPaddingLeft: number | undefined
  virtualPaddingRight: number | undefined
  columnOrder: string[]
}

function TableHeadRow({
  columnVirtualizer,
  headerGroup,
  virtualPaddingLeft,
  virtualPaddingRight,
  columnOrder,
}: TableHeadRowProps) {
  const virtualColumns = columnVirtualizer.getVirtualItems()
  return (
    <tr
      key={headerGroup.id}
      style={{ display: 'flex', width: '100%' }}
      className="bg-gray-100"
    >
      <SortableContext
        items={columnOrder}
        strategy={horizontalListSortingStrategy}
      >
        {virtualPaddingLeft ? (
          //fake empty column to the left for virtualization scroll padding
          <th style={{ display: 'flex', width: virtualPaddingLeft }} />
        ) : null}
        {virtualColumns.map((virtualColumn) => {
          const header = headerGroup.headers[virtualColumn.index]
          return <TableHeadCell key={header.id} header={header} />
        })}
        {virtualPaddingRight ? (
          //fake empty column to the right for virtualization scroll padding
          <th style={{ display: 'flex', width: virtualPaddingRight }} />
        ) : null}
      </SortableContext>
    </tr>
  )
}

interface TableHeadCellProps {
  header: Header<CSVRow, unknown>
}

function TableHeadCell({ header }: TableHeadCellProps) {
  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      id: header.column.id,
    })

  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    position: 'relative',
    transform: CSS.Translate.toString(transform), // translate instead of transform to avoid squishing
    transition: 'width transform 0.2s ease-in-out',
    whiteSpace: 'nowrap',
    width: header.column.getSize(),
    zIndex: isDragging ? 1 : 0,
    display: 'flex',
    border: '0.5px solid #E2E8F0',
  }

  if (header.column.id === 'select') {
    return (
      <th
        key={header.id}
        colSpan={header.colSpan}
        ref={setNodeRef}
        style={style}
      >
        <div className="flex flex-row items-start justify-center text-sm p-2 gap-2">
          {flexRender(header.column.columnDef.header, header.getContext())}
        </div>
      </th>
    )
  }

  return (
    <th key={header.id} colSpan={header.colSpan} ref={setNodeRef} style={style}>
      <div className="flex flex-row items-start justify-center text-sm p-2 gap-2">
        <div {...attributes} {...listeners}>
          <GripVerticalIcon size={16} />
        </div>
        <div
          className={cn({
            'select-none cursor-pointer': header.column.getCanSort(),
          })}
          onClick={header.column.getToggleSortingHandler()}
        >
          {{
            asc: <ArrowUpWideNarrowIcon size={16} />,
            desc: <ArrowDownWideNarrowIcon size={16} />,
          }[header.column.getIsSorted() as string] ?? (
            <ArrowUpDownIcon size={16} />
          )}
        </div>
        {flexRender(header.column.columnDef.header, header.getContext())}
      </div>
    </th>
  )
}

interface TableBodyProps {
  columnVirtualizer: Virtualizer<HTMLDivElement, HTMLTableCellElement>
  table: Table<CSVRow>
  tableContainerRef: React.RefObject<HTMLDivElement | null>
  virtualPaddingLeft: number | undefined
  virtualPaddingRight: number | undefined
}

function TableBody({
  columnVirtualizer,
  table,
  tableContainerRef,
  virtualPaddingLeft,
  virtualPaddingRight,
}: TableBodyProps) {
  const { rows } = table.getRowModel()

  const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
    count: rows.length,
    estimateSize: () => 35,
    getScrollElement: () => tableContainerRef.current,
    overscan: 5,
  })

  const virtualRows = rowVirtualizer.getVirtualItems()

  return (
    <tbody
      style={{
        display: 'grid',
        height: `${rowVirtualizer.getTotalSize()}px`, //tells scrollbar how big the table is
        position: 'relative', //needed for absolute positioning of rows
      }}
    >
      {virtualRows.map((virtualRow) => {
        const row = rows[virtualRow.index] as Row<CSVRow>

        return (
          <TableBodyRow
            columnVirtualizer={columnVirtualizer}
            key={row.id}
            row={row}
            rowVirtualizer={rowVirtualizer}
            virtualPaddingLeft={virtualPaddingLeft}
            virtualPaddingRight={virtualPaddingRight}
            virtualRow={virtualRow}
          />
        )
      })}
    </tbody>
  )
}

interface TableBodyRowProps {
  columnVirtualizer: Virtualizer<HTMLDivElement, HTMLTableCellElement>
  row: Row<CSVRow>
  rowVirtualizer: Virtualizer<HTMLDivElement, HTMLTableRowElement>
  virtualPaddingLeft: number | undefined
  virtualPaddingRight: number | undefined
  virtualRow: VirtualItem
}

function TableBodyRow({
  columnVirtualizer,
  row,
  rowVirtualizer,
  virtualPaddingLeft,
  virtualPaddingRight,
  virtualRow,
}: TableBodyRowProps) {
  const visibleCells = row.getVisibleCells()
  const virtualColumns = columnVirtualizer.getVirtualItems()
  return (
    <tr
      data-index={virtualRow.index} //needed for dynamic row height measurement
      ref={(node) => rowVirtualizer.measureElement(node)} //measure dynamic row height
      key={row.id}
      style={{
        display: 'flex',
        position: 'absolute',
        transform: `translateY(${virtualRow.start}px)`, //this should always be a `style` as it changes on scroll
        width: '100%',
        height: '35px',
      }}
    >
      {virtualPaddingLeft ? (
        //fake empty column to the left for virtualization scroll padding
        <td style={{ display: 'flex', width: virtualPaddingLeft }} />
      ) : null}
      {virtualColumns.map((vc) => {
        const cell = visibleCells[vc.index]
        return <TableBodyCell key={cell.id} cell={cell} />
      })}
      {virtualPaddingRight ? (
        //fake empty column to the right for virtualization scroll padding
        <td style={{ display: 'flex', width: virtualPaddingRight }} />
      ) : null}
    </tr>
  )
}

interface TableBodyCellProps {
  cell: Cell<CSVRow, unknown>
}

function TableBodyCell({ cell }: TableBodyCellProps) {
  const { isDragging, setNodeRef, transform } = useSortable({
    id: cell.column.id,
  })

  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    position: 'relative',
    transform: CSS.Translate.toString(transform), // translate instead of transform to avoid squishing
    transition: 'width transform 0.2s ease-in-out',
    zIndex: isDragging ? 1 : 0,
    display: 'flex',
    width: cell.column.getSize(),
    border: '0.5px solid #E2E8F0',
  }

  return (
    <td key={cell.id} style={style} ref={setNodeRef}>
      <div className="h-full w-full">
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </div>
    </td>
  )
}

function IndeterminateCheckbox({
  indeterminate,
  className = '',
  ...rest
}: { indeterminate?: boolean } & React.HTMLProps<HTMLInputElement>) {
  const ref = useRef<HTMLInputElement>(null!)

  useEffect(() => {
    if (typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !rest.checked && indeterminate
    }
  }, [ref, indeterminate])

  return (
    <input
      type="checkbox"
      ref={ref}
      className={className + ' cursor-pointer'}
      {...rest}
    />
  )
}
